import { __extends } from "tslib";
import { Rectangle, Point, Vector3D } from "@awayjs/core";
import { Filter3DTaskBase } from "./Filter3DTaskBase";
import { ContextGLProgramType } from '../../base/ContextGLProgramType';
var Filter3DThresholdTask = /** @class */ (function (_super) {
    __extends(Filter3DThresholdTask, _super);
    function Filter3DThresholdTask() {
        var _this = _super.call(this) || this;
        _this._opDecode = {
            "<": "slt ",
            "<=": "sge ",
            ">": "slt ",
            ">=": "sge ",
            "==": "seq ",
            "!=": "sne "
        };
        _this._thDecode = {
            "<": false,
            "<=": true,
            ">": true,
            ">=": false,
            "==": false,
            "!=": false
        };
        _this._operation = "<";
        _this._threshold = 0;
        _this._thresholdRGBA = new Vector3D();
        _this._color = 0;
        _this._colorRGBA = new Vector3D();
        _this._mask = 0;
        _this._maskRGBA = new Vector3D();
        _this._copySource = false;
        _this._decodeVector = new Vector3D(65025.0, 255.0, 1.0, 16581375.0);
        _this.rect = new Rectangle();
        _this.destPoint = new Point();
        _this._vertexConstantData = new Float32Array([0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0]);
        _this._fragmentConstantData = new Float32Array([0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 65025.0, 255.0, 1.0, 16581375.0]);
        return _this;
    }
    Object.defineProperty(Filter3DThresholdTask.prototype, "operation", {
        get: function () {
            return this._operation;
        },
        set: function (value) {
            if (this._operation == value)
                return;
            this._operation = value;
            this._op = this._opDecode[value];
            this._th = this._thDecode[value];
            this.invalidateProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Filter3DThresholdTask.prototype, "threshold", {
        get: function () {
            return this._threshold;
        },
        set: function (value) {
            if (this._threshold == value)
                return;
            this._threshold = value;
            this._thresholdRGBA.x = ((value >> 16) & 0xff) / 0xff;
            this._thresholdRGBA.y = ((value >> 8) & 0xff) / 0xff;
            this._thresholdRGBA.z = (value & 0xff) / 0xff;
            this._thresholdRGBA.w = ((value >> 24) & 0xff) / 0xff;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Filter3DThresholdTask.prototype, "color", {
        get: function () {
            return this._color;
        },
        set: function (value) {
            if (this._color == value)
                return;
            this._color = value;
            //pre-multiply alpha
            var alpha = ((value >> 24) & 0xff) / 0xff;
            this._colorRGBA.x = alpha * ((value >> 16) & 0xff) / 0xff;
            this._colorRGBA.y = alpha * ((value >> 8) & 0xff) / 0xff;
            this._colorRGBA.z = alpha * (value & 0xff) / 0xff;
            this._colorRGBA.w = alpha;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Filter3DThresholdTask.prototype, "mask", {
        get: function () {
            return this._mask;
        },
        set: function (value) {
            if (this._mask == value)
                return;
            this._mask = value;
            this._maskRGBA.x = ((value >> 16) & 0xff) / 0xff;
            this._maskRGBA.y = ((value >> 8) & 0xff) / 0xff;
            this._maskRGBA.z = (value & 0xff) / 0xff;
            this._maskRGBA.w = ((value >> 24) & 0xff) / 0xff;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Filter3DThresholdTask.prototype, "copySource", {
        get: function () {
            return this._copySource;
        },
        set: function (value) {
            if (this._copySource == value)
                return;
            this._copySource = value;
            this.invalidateProgram();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Filter3DThresholdTask.prototype, "sourceTexture", {
        get: function () {
            return this._mainInputTexture;
        },
        set: function (value) {
            this._mainInputTexture = value;
        },
        enumerable: true,
        configurable: true
    });
    Filter3DThresholdTask.prototype.getVertexCode = function () {
        var temp1 = this._registerCache.getFreeVertexVectorTemp();
        var rect = this._registerCache.getFreeVertexConstant();
        var position = this._registerCache.getFreeVertexAttribute();
        this._positionIndex = position.index;
        var offset = this._registerCache.getFreeVertexConstant();
        var uv = this._registerCache.getFreeVertexAttribute();
        this._uvIndex = uv.index;
        this._uvVarying = this._registerCache.getFreeVarying();
        var code;
        code = "mul " + temp1 + ".xy, " + position + ", " + rect + ".zw\n" +
            "add " + temp1 + ".xy, " + temp1 + ", " + rect + ".xy\n" +
            "mov " + temp1 + ".w, " + position + ".w\n" +
            "mov op, " + temp1 + "\n" +
            "add " + this._uvVarying + ", " + uv + ", " + offset + ".xy\n";
        return code;
    };
    Filter3DThresholdTask.prototype.getFragmentCode = function () {
        var temp1 = this._registerCache.getFreeFragmentVectorTemp();
        this._registerCache.addFragmentTempUsages(temp1, 1);
        var inputTexture = this._registerCache.getFreeTextureReg();
        this._inputTextureIndex = inputTexture.index;
        var threshold = this._registerCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = threshold.index;
        var color = this._registerCache.getFreeFragmentConstant();
        var mask = this._registerCache.getFreeFragmentConstant();
        var decode = this._registerCache.getFreeFragmentConstant();
        var code;
        if (this._copySource) {
            var temp2 = this._registerCache.getFreeFragmentVectorTemp();
            this._registerCache.addFragmentTempUsages(temp2, 1);
            var temp3 = this._registerCache.getFreeFragmentVectorTemp();
            this._registerCache.addFragmentTempUsages(temp3, 1);
            code = "tex " + temp1 + ", " + this._uvVarying + ", " + inputTexture + " <2d,linear,clamp>\n" +
                "mul " + temp2 + ", " + temp1 + ", " + mask + "\n" +
                "dp4 " + temp2 + ", " + temp2 + ", " + decode + "\n" +
                this._op + temp2 + ", " + (this._th ? threshold : temp2) + ", " + (this._th ? temp2 : threshold) + "\n" +
                "sub " + temp3 + ", " + decode + ".zzzz, " + temp2 + "\n" +
                "mul " + temp2 + ", " + temp2 + ", " + color + "\n" +
                "mul " + temp3 + ", " + temp3 + ", " + temp1 + "\n" +
                "add " + temp1 + ", " + temp2 + ", " + temp3 + "\n" +
                "mov oc, " + temp1 + "\n";
            this._registerCache.removeFragmentTempUsage(temp3);
            this._registerCache.removeFragmentTempUsage(temp2);
        }
        else {
            code = "tex " + temp1 + ", " + this._uvVarying + ", " + inputTexture + " <2d,linear,clamp>\n" +
                "mul " + temp1 + ", " + temp1 + ", " + mask + "\n" +
                "dp4 " + temp1 + ", " + temp1 + ", " + decode + "\n" +
                this._op + temp1 + ", " + (this._th ? threshold : temp1) + ", " + (this._th ? temp1 : threshold) + "\n" +
                "mul " + temp1 + ", " + temp1 + ", " + color + "\n" +
                "mov oc, " + temp1 + "\n";
        }
        this._registerCache.removeFragmentTempUsage(temp1);
        return code;
    };
    Filter3DThresholdTask.prototype.getMainInputTexture = function (stage) {
        return this._mainInputTexture;
    };
    Filter3DThresholdTask.prototype.activate = function (stage, projection, depthTexture) {
        var index = this._positionIndex;
        var data = this._vertexConstantData;
        data[index] = (2 * this.destPoint.x + this.rect.width) / this._target.width - 1;
        data[index + 1] = (2 * this.destPoint.y + this.rect.height) / this._target.height - 1;
        data[index + 2] = this.rect.width / this._target.width;
        data[index + 3] = this.rect.height / this._target.height;
        data[index + 4] = this.rect.x / this._mainInputTexture.width;
        data[index + 5] = this.rect.y / this._mainInputTexture.height;
        var tVector = new Vector3D(this._thresholdRGBA.x * this._maskRGBA.x, this._thresholdRGBA.y * this._maskRGBA.y, this._thresholdRGBA.z * this._maskRGBA.z, this._thresholdRGBA.w * this._maskRGBA.w);
        var tValue = tVector.dotProduct(this._decodeVector);
        index = this._fragmentConstantsIndex;
        data = this._fragmentConstantData;
        data[index] = tValue;
        data[index + 1] = tValue;
        data[index + 2] = tValue;
        data[index + 3] = tValue;
        data[index + 4] = this._colorRGBA.x;
        data[index + 5] = this._colorRGBA.y;
        data[index + 6] = this._colorRGBA.z;
        data[index + 7] = this._colorRGBA.w;
        data[index + 8] = this._maskRGBA.x;
        data[index + 9] = this._maskRGBA.y;
        data[index + 10] = this._maskRGBA.z;
        data[index + 11] = this._maskRGBA.w;
        var context = stage.context;
        context.setProgramConstantsFromArray(ContextGLProgramType.VERTEX, this._vertexConstantData);
        context.setProgramConstantsFromArray(ContextGLProgramType.FRAGMENT, this._fragmentConstantData);
    };
    Filter3DThresholdTask.prototype.deactivate = function (stage) {
    };
    return Filter3DThresholdTask;
}(Filter3DTaskBase));
export { Filter3DThresholdTask };
